<template>
<div>
    <div
        :class="[isChecked ? 'bg-primary-5' : 'bg-white']"
        class="flex items-center gap-16 cursor-pointer hover:bg-primary-5 px-24 py-12"
        @click="handleClick"
    >
        <checkbox
            :checked="isChecked"
            class="pointer-events-auto"
            @change="toggleApplication"
        />

        <div class="w-full flex items-center">
            <avatar :color="application.stageColor" :image="application.image_url" class="mr-16"
                    shadow="shadow-sm">
                {{ application.initials }}
            </avatar>
            <div class="flex-grow text-black space-y-4">
                <div class="flex items-center gap-12 leading-none">
                    <div class="flex items-center gap-4">
                        <svg-icon
                            v-if="application.favorite"
                            class="text-semantic-warning w-16 h-16"
                            type="star-filled"
                        />
                        <p class="font-semibold text-body">{{ application.fullName }}</p>
                    </div>
                    <div class="flex items-center gap-4">
                        <svg-icon class="w-18 h-18 fill-primary-80" type="schedule" />
                        <p class="text-primary-80 text-body-sm">{{ createdAt }}</p>
                    </div>
                    <svg-icon
                        v-if="isSubscribed"
                        class="w-18 h-18 fill-semantic-info"
                        type="notifications"
                    />
                </div>
                <p class="text-body-sm">{{ jobTitle }}</p>
            </div>
            <div v-if="columns.includes('expires')" class="flex flex-col items-center">
                <p class="text-body-sm mb-4 text-primary-80">{{ application.expires_in < 0 ? $t('applications.expired_since') : $t('applications.expires_in') }}</p>
                <tag
                    :display="application.expires_in < 0 ? 'warning' : application.expires_in < 31 ? 'info' : 'success'"
                    no-icon
                >
                    {{ $t('applications.days', {days: Math.abs(application.expires_in)}) }}
                </tag>
            </div>
            <div
                v-if="columns.includes('referrer') && application.referrer_source === 'interim' && application.interim"
                class="flex items-center text-primary-80 mr-40"
            >
                <svg-icon class="h-16 w-16 fill-primary-80" type="interim"></svg-icon>
                <p class="first-letter:uppercase ml-8 inline-block">{{ application.interim.name }}</p>
            </div>
            <div class="flex items-center gap-24 ml-8">
                <progress-icon
                    v-if="columns.includes('progress')"
                    :rejected="application.rejected"
                    :stage="application.stage"
                ></progress-icon>
                <div
                    v-if="columns.includes('score')"
                    class="flex flex-col items-center"
                >
                    <strong>
                        {{ application.score ? application.score + " %" : "-" }}
                    </strong>
                </div>
                <div
                    class="pointer-events-auto"
                    @click.stop
                >
                    <simple-dropdown
                        v-if="can.dropdown"
                        ref="ddmActions"
                        position="right"
                    >
                        <template #trigger>
                            <button role="button" class="py-8">
                                <svg-icon class="w-20 h-20 text-primary-80 group-hover:text-primary" type="more-horiz"/>
                            </button>
                        </template>
                        <template #content>
                            <nav
                                aria-orientation="vertical"
                                class="bg-white relative rounded shadow"
                                role="menu"
                            >
                                <dropdown-item
                                    icon="star"
                                    :input-label="application.favorite ? $t('applications.remove_favorite') : $t('applications.make_favorite')"
                                    v-if="can.update"
                                    @click="setFavorite"
                                />
                                <dropdown-item
                                    icon="edit"
                                    :input-label="$t('general.edit')"
                                    v-if="can.update"
                                    @click="selectAction('edit')"
                                />
                                <dropdown-item
                                    danger
                                    icon="delete"
                                    :input-label="$t('general.delete')"
                                    v-if="can.delete"
                                    @click="selectAction('delete')"
                                />
                            </nav>
                        </template>
                    </simple-dropdown>
                </div>
            </div>
        </div>

    </div>

    <popup-modal-confirm
            v-if="can.delete && deleting"
            :message="$t('applications.delete_modal_description')"
            show-exclamation
            :working="performingDeleteRequest"
            @close="cancelDelete"
            @confirm="deleteApplication"
            >
    </popup-modal-confirm>

    <teleport to="body">
        <application-editor
                v-if="can.update && editing"
                :application="application"
                :stage="application.stage"
                @close="stopEdit"
                ></application-editor>
    </teleport>
</div>
</template>

<script>
import * as applicationService from '../services/ApplicationService';
import Application from '../models/Application';
import Checkbox from './forms/Checkbox.vue';
import PopupModalConfirm from './ui/PopupModalConfirm.vue';
import Score from '../components/ui/Score.vue';
import SimpleDropdown from './ui/SimpleDropdown.vue';
import SvgIcon from './ui/SvgIcon.vue';
import moment from 'moment';
import Avatar from './ui/Avatar.vue';
import ApplicationEditor from './ui/ApplicationEditor.vue';
import ProgressIcon from './ui/ProgressIcon.vue';
import Tag from "./ui/Tag.vue";
import DropdownItem from "./ui/DropdownItem.vue";
import {mapStores} from "pinia";
import {mapRepos} from "pinia-orm";
import {useMainStore} from "../state/store";
import {useApplicationsStore} from "../state/modules/applications";

export default {
    components: {
        DropdownItem,
        Tag,
        'checkbox': Checkbox,
        'popup-modal-confirm': PopupModalConfirm,
        'score': Score,
        'simple-dropdown': SimpleDropdown,
        'svg-icon': SvgIcon,
        'avatar': Avatar,
        'application-editor': ApplicationEditor,
        'progress-icon': ProgressIcon,
    },
    data: function () {
        return {
            deleting: false,
            editing: false,
            performingDeleteRequest: false,
            columns: this.inGdprContext ? ['expires'] : ['referrer', 'progress', 'score'],
            storeModule: this.inGdprContext ? 'gdpr' : 'applications',
        };
    },
    props: {
        application: Object,
        clickToRoute: Boolean,
        inGdprContext: Boolean,
        sequence: Array,
    },
    computed: {
        ...mapStores(useMainStore, useApplicationsStore),
        ...mapRepos({applicationRepo: Application}),
        locale() {
            return this.mainStore.locale;
        },
        dateFormat() {
            return this.mainStore.dateFormat;
        },
        currentUser() {
            return this.mainStore.currentUser;
        },
        can() {
            return {
                update: this.$can('applications.update'),
                delete: this.$can('applications.delete'),
                dropdown: this.$can(['applications.update', 'applications.delete']),
            };
        },
        createdAt() {
            return moment(this.application.created_at).locale(this.locale).format(this.dateFormat);
        },
        isChecked() {
            for (let x of this.applicationsModuleStore.checkedApplications) {
                if (x.id == this.application.id) {
                    return true;
                }
            }

            return false;
        },
        jobTitle() {
            if (this.application.stage && this.application.stage.job) {
                return this.translated(this.application.stage.job.title, this.locale, true);
            }

            return '';
        },
        stageTitle() {
            return this.application.stage.name;
        },
        isSubscribed() {
            return this.application.subscriptions.some(subscription => {
                return subscription.user_id === this.currentUser.id;
            });
        },
    },
    methods: {
        cancelDelete() {
            this.deleting = false;
        },
        deleteApplication() {
            this.performingDeleteRequest = true;

            applicationService
                .destroy(this.application.id)
                .then(() => {
                    this.applicationRepo.destroy(this.application.id);
                })
                .then(() => {
                    this.totalApplications--;
                    this.deleting = false;
                    this.performingDeleteRequest = false;
                })
            ;
        },
        selectAction(action) {
            this.$refs.ddmActions.closeMe();

            if (action == 'delete') {
                this.deleting = true;
            }

            if (action == 'edit') {
                this.editing = true;
            }
        },
        toggleApplication(isChecked) {
            let applicationId = this.application.id;
            this.applicationsModuleStore.checkApplication({applicationId, isChecked});
        },
        handleClick() {
            if (this.clickToRoute) {
                this.applicationsModuleStore.setSequence(this.sequence);
                this.$router.push({name: 'application', params: {id: this.application.id}});
            } else {
                this.toggleApplication(!this.isChecked);
            }
        },
        stopEdit() {
            this.editing = false;
        },
        setFavorite() {
            this.$refs.ddmActions.close();
            this.applicationsModuleStore.setFavorite({
                applicationId: this.application.id,
                favorite: this.application.favorite
            });
        },
    },
};
</script>
